const METRICS = [
  {
    code: "/ AI Demand",
    value: "+220%",
    label: "DC power demand growth",
    detail:
      "Global data center power demand projected to rise 220% by 2030 versus 2023, up from a 165% estimate just one year ago.",
    urgency: "CRITICAL",
    gaugeValue: 33,
    gaugeLabel: "Forecast revised UP 33% YoY",
    source: "Goldman 2026",
  },
  {
    code: "/ Infrastructure",
    value: "$3T+",
    label: "Sector investment by 2030",
    detail:
      "Hyperscale, colocation, and edge buildouts. JLL frames a 14% CAGR supercycle through 2030 across power, cooling, network, and shell.",
    urgency: "HIGH",
    gaugeValue: 14,
    gaugeLabel: "Sector CAGR through 2030",
    source: "JLL 2026",
  },
  {
    code: "/ Talent Gap",
    value: "53%",
    label: "Operators citing hiring difficulty",
    detail:
      "Biggest gaps sit in junior and mid-level operations, mechanical, and electrical roles. More than half the industry cannot fill them.",
    urgency: "CRITICAL",
    gaugeValue: 53,
    gaugeLabel: "Operators reporting difficulty",
    source: "Uptime 2026",
  },
  {
    code: "/ Window",
    value: "5 yrs",
    label: "To build the workforce",
    detail:
      "The next half-decade defines who builds, and who watches, the AI-era backbone.",
    urgency: "NOW",
    gaugeValue: 40,
    gaugeLabel: "Year 2 of 5, build window elapsed",
    source: "DCYB outlook",
  },
];

const URGENCY_STYLES = {
  CRITICAL: "text-[#B91C1C] border-[#B91C1C]/30 bg-[#B91C1C]/5",
  HIGH: "text-[#B45309] border-[#B45309]/30 bg-[#B45309]/5",
  NOW: "text-[#0F1117] border-[#0F1117]/40 bg-[#0F1117]/5",
};

export default function WhyNow() {
  return (
    <section
      id="why-now"
      data-testid="why-now-section"
      className="border-b hairline relative bg-[#F4F5F7] overflow-hidden"
    >
      {/* Ghosted brand watermark */}
      <img
        src="/dcyb-logo.png"
        alt=""
        aria-hidden
        draggable="false"
        className="absolute -right-20 top-10 w-[480px] h-auto opacity-[0.05] pointer-events-none select-none"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-32 relative">
        <div className="grid grid-cols-12 gap-y-12 gap-x-6 mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div data-testid="why-now-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6">
              / Why now
            </div>
            <h2 className="font-display text-[1.7rem] min-[390px]:text-3xl sm:text-[2.6rem] lg:text-[2.6rem] xl:text-[3.1rem] leading-[1.14] sm:leading-[1.14] lg:leading-[1.14] tracking-tight font-extrabold text-[#0F1117] text-balance">
              The compute era is <span className="whitespace-nowrap">power-bound</span> and <span className="whitespace-nowrap">people-bound.</span>
            </h2>
          </div>
          <div className="col-span-12 lg:col-span-5 lg:pl-8 lg:border-l hairline lg:self-end">
            <p className="text-base sm:text-lg text-[#3D4049] leading-relaxed max-w-2xl">
              {"Capital is flowing. Permits are clearing. Substations are being built. The constraint is no longer dollars or designs. It is the practitioners who can stand sites up, run them at 99.999%, and train the next class behind them."}
            </p>
            <p className="mt-4 text-base text-[#5F6168] leading-relaxed max-w-2xl">
              DCYB closes that gap.
            </p>
          </div>
        </div>

        {/* Data grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border-l border-t hairline bg-white">
          {METRICS.map((m) => (
            <div
              key={m.code}
              data-testid={`metric-${m.code.replace(/[^a-zA-Z0-9]/g, "")}`}
              className="border-r border-b hairline p-8 md:p-10 bg-gradient-to-b from-white to-[#F2F3F5] hover:from-white hover:to-[#E6E8EB] transition-colors min-h-[260px] flex flex-col"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-mono-label text-[11px] text-[#71747B]">
                  {m.code}
                </span>
                <span
                  data-testid={`metric-urgency-${m.code.replace(/[^a-zA-Z0-9]/g, "")}`}
                  className={`font-mono-label text-[9px] tracking-[0.16em] px-2 py-0.5 border rounded-full ${URGENCY_STYLES[m.urgency] || URGENCY_STYLES.HIGH}`}
                >
                  {m.urgency}
                </span>
              </div>
              <div className="font-display text-5xl md:text-6xl font-extrabold text-chrome tracking-tight">
                {m.value}
              </div>
              <div className="font-mono-label text-[11px] text-[#0F1117] mt-2">
                {m.label}
              </div>
              <p className="mt-6 text-sm text-[#52555B] leading-relaxed flex-1">
                {m.detail}
              </p>
              {/* Real-data gauge */}
              <div className="mt-6 space-y-2">
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-[3px] bg-[#E3E5E9] relative overflow-hidden">
                    <div
                      className="h-full chrome-bar"
                      style={{ width: `${m.gaugeValue}%` }}
                    />
                  </div>
                  <span className="font-mono-label text-[10px] text-[#0F1117] font-semibold w-10 text-right">
                    {`${m.gaugeValue}%`}
                  </span>
                </div>
                <div className="font-mono-label text-[9px] tracking-[0.12em] text-[#71747B] leading-tight">
                  {m.gaugeLabel} · {m.source}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
